import React, { useState } from 'react';
import { CheckCircleIcon } from 'lucide-react';
const RECIPIENT = 'info@yetudesignlab.com';
const SERVICES = ['Branding', 'Web design & build', 'Marketing', 'Creative consulting'];
export function ContactForm() {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState<string[]>(['Branding']);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toggle = (service: string) => setSelected(current => current.includes(service) ? current.filter(item => item !== service) : [...current, service]);
  const buildMailto = () => {
    const subject = `Free consultation request — ${name || 'New enquiry'}`;
    const body = [`Name: ${name}`, `Company: ${company || '—'}`, `Email: ${email}`, `Services of interest: ${selected.join(', ') || '—'}`, '', 'Project details:', message].join('\n');
    return `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${RECIPIENT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name,
          company: company || 'Not provided',
          email,
          services: selected.join(', ') || 'None selected',
          message,
          _subject: `New consultation request from ${name}`
        })
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Fallback to mailto if the fetch fails (e.g., blocked by adblocker)
      window.location.href = buildMailto();
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (submitted) {
    return <div role="status" className="flex flex-col items-start justify-center gap-3 rounded-2xl bg-[#F2F2F2] p-10" data-id="element-9" data-mp="30xucug8wm06s">
        <CheckCircleIcon className="h-8 w-8 text-[#0000FE]" aria-hidden="true" data-id="element-10" data-mp="2d8rkoegn8oy1" />
        <h3 className="text-xl font-semibold text-[#2D2D2D]" data-id="element-11" data-mp="2987rflz6y5h6">
          Thanks — message received.
        </h3>
        <p className="text-sm leading-6 text-[#595959]" data-id="element-12" data-mp="2mzm7bjxsxqk0">
          We've received your details and will get back to you within 24 hours with next steps.
        </p>
        <button onClick={() => {
        setSubmitted(false);
        setName('');
        setCompany('');
        setEmail('');
        setMessage('');
        setSelected(['Branding']);
      }} className="mt-2 text-sm font-semibold text-[#0000FE] hover:underline" data-id="element-13" data-mp="1uh8mckxkdz2c">
          Send another message
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="flex flex-col gap-5 rounded-2xl bg-[#F8F8F8] p-6 lg:p-8" data-id="element-14" data-mp="3s6dr0sfmazf6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-15" data-mp="102rx1u648qh">
        <div className="flex flex-col gap-2" data-id="element-16" data-mp="oocck0fnaqg1">
          <label htmlFor="name" className="text-sm font-medium text-[#2D2D2D]" data-id="element-17" data-mp="opnedupj5o4i">
            Name
          </label>
          <input id="name" name="name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Jane" className="field" data-id="element-18" data-mp="socye652q3x4" />
        </div>
        <div className="flex flex-col gap-2" data-id="element-19" data-mp="2zfc5m9lc07n7">
          <label htmlFor="company" className="text-sm font-medium text-[#2D2D2D]" data-id="element-20" data-mp="34g039e42fmqe">
            Company
          </label>
          <input id="company" name="company" type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Inc." className="field" data-id="element-21" data-mp="3jr7x6zbpg4eh" />
        </div>
      </div>

      <div className="flex flex-col gap-2" data-id="element-22" data-mp="35zbchgcd8ws4">
        <label htmlFor="email" className="text-sm font-medium text-[#2D2D2D]" data-id="element-23" data-mp="35wfclmgyjy11">
          Company Email
        </label>
        <input id="email" name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@acme.com" className="field" data-id="element-24" data-mp="2ht3v6orrlqn" />
      </div>

      <fieldset className="flex flex-col gap-3" data-id="element-25" data-mp="3oqublc39rqov">
        <legend className="text-sm font-medium text-[#2D2D2D]" data-id="element-26" data-mp="2i8xqa6ggps5g">
          How can we help you?
        </legend>
        <div className="flex flex-wrap gap-2" data-id="element-27" data-mp="2d8jfwhre2649">
          {SERVICES.map(service => {
          const active = selected.includes(service);
          return <button key={service} type="button" aria-pressed={active} onClick={() => toggle(service)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? 'border-[#0000FE] bg-[#0000FE] text-white' : 'border-[#D9D9D9] bg-white text-[#2D2D2D] hover:border-[#0000FE]'}`} data-id="element-28" data-mp="284hblgepyh38">
                {service}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2" data-id="element-29" data-mp="2vs8mqgoiwo2x">
        <label htmlFor="message" className="text-sm font-medium text-[#2D2D2D]" data-id="element-30" data-mp="30toa4fe6b6oh">
          Tell us about your project
        </label>
        <textarea id="message" name="message" rows={4} required value={message} onChange={e => setMessage(e.target.value)} placeholder="What are you building, and when do you need it?" className="field" data-id="element-31" data-mp="11hv9m8s6fj70" />
      </div>

      <button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center rounded-full bg-[#0000FE] px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-[#0000d6] disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-32" data-mp="thfg7p0q92hv">
        {isSubmitting ? 'Sending...' : 'Send message'}
      </button>
    </form>;
}